<template>
  <div class="pre-projects">
    <template v-if="hasPreProjectsAccess">
      <div class="pre-projects__header">
        <div class="pre-projects__header-content">
          <!-- Phones: the view tabs don't fit, so the title names the view and switches it. -->
          <h1 v-if="switchesView" class="pre-projects__header-title">
            <BaseViewSwitcher
              :page="$t('page.pre-projects.title')"
              :view="activeViewLabel"
              @click="$refs.views.openViewsSheet()"
            />
          </h1>
          <h1 v-else class="pre-projects__header-title">{{ $t('page.pre-projects.title') }}</h1>
        </div>
        <!-- Phones: a new project from the header, as a new opportunity (the filters row has no room). -->
        <BaseButton
          v-if="isPhone && !isTrialRestricted"
          class="pre-projects__new"
          has-icon-left
          @click.native="onAddPreProject"
        >
          <BaseIcon name="plus" />
          <span class="pre-projects__new-label">{{ $t('page.pre-projects.form.add-project') }}</span>
        </BaseButton>
      </div>

      <div class="pre-projects__main">
        <div
          class="pre-projects__filters-panel"
          :class="{ 'pre-projects__filters-panel--disabled': isTrialRestricted }"
          :aria-disabled="isTrialRestricted ? 'true' : null"
        >
          <PreProjectViews ref="views" @select="onSelectView" @refresh="onFiltersChange">
            <template #filters>
              <!-- Keyed by the active view: switching tabs remounts the filter bar, resetting
                   all transient UI state (search text, open popovers) and swallowing any
                   pending debounced search emit that would re-apply a stale query after the
                   view's filters were loaded. Phones: the search and the filters on one line. -->
              <PhoneFilterBar
                v-if="isPhone"
                :key="activeViewId"
                :active-count="activeFilterCount"
                @clear-all="onClearAllFilters"
              >
                <template #search>
                  <PreProjectsSearchBar @change="onFiltersChange" />
                </template>
                <template #filters>
                  <PreProjectsFilters @change="onFiltersChange" />
                </template>
              </PhoneFilterBar>
              <PreProjectsFilters v-else :key="activeViewId" @change="onFiltersChange" @add-project="onAddPreProject" />
            </template>
            <!-- Search sits under the view meta (Prédéfinie badge / save-state row). -->
            <template v-if="!isPhone" #search>
              <PreProjectsSearchBar :key="`search-${activeViewId}`" @change="onFiltersChange" />
            </template>
          </PreProjectViews>
        </div>

        <!-- Trial preview: first page only, filters/search/views and pagination inert. -->
        <BaseInfoBanner
          v-if="isTrialRestricted"
          class="pre-projects__trial-banner"
          tone="warning"
          preset="soft"
          :title="$t('upsell.pre-projects.trial-banner-title')"
          :description="$t('upsell.pre-projects.trial-banner-description')"
        >
          <template #icon>
            <BaseIcon name="lock" />
          </template>
          <template #action>
            <button type="button" @click="onBookCall">
              {{ $t('upsell.actions.book-call') }}
            </button>
          </template>
        </BaseInfoBanner>

        <PreProjectsDataTable
          :pre-projects="preProjects"
          :trial-restricted="isTrialRestricted"
          @refresh="onRefresh"
          @page-changed="fetchPreProjects"
          @load-more="loadMore"
          @edit="onEditPreProject"
          @delete="onConfirmDeletePreProject"
        />
      </div>

      <PreProjectFormDialog ref="preProjectFormDialog" @saved="onPreProjectSaved" @sources-changed="onRefresh" />

      <!-- Delete confirmation for a custom pre-project -->
      <BaseDialog
        centered
        ref="deletePreProjectDialog"
        contentClass="pre-project-delete-confirm"
        :show-actions="true"
        @closeModal="closeDeletePreProject"
        :onClose="closeDeletePreProject"
      >
        <template slot="title">{{ $t('page.pre-projects.form.delete-title') }}</template>
        <template slot="content">
          <p class="pre-projects__delete-text">
            <i18n path="page.pre-projects.form.delete-confirm" tag="span">
              <template v-slot:title>
                <strong>{{ deletingPreProject ? deletingPreProject.title : '' }}</strong>
              </template>
            </i18n>
          </p>
        </template>
        <template slot="actions">
          <div class="pre-projects__delete-actions">
            <BaseButton isOutlined isSquare @click.native="closeDeletePreProject">
              {{ $t('page.tenders.views.cancel') }}
            </BaseButton>
            <BaseButton isDanger @click.native="onDeletePreProject">
              {{ $t('page.tenders.views.delete-action') }}
            </BaseButton>
          </div>
        </template>
      </BaseDialog>
    </template>

    <BlurredFeatureOverlay
      v-else
      :title="$t('upsell.pre-projects.title')"
      :lede="$t('upsell.pre-projects.lede')"
      :benefits="preProjectsBenefits"
      :badge-label="$t('upsell.from-plus-plan')"
      full-viewport
    >
      <div class="pre-projects__blur-content">
        <PreProjectsSamplePreview />
      </div>
    </BlurredFeatureOverlay>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseInfoBanner from '@/components/base/BaseInfoBanner.vue';
import BaseViewSwitcher from '@/components/base/BaseViewSwitcher.vue';
import PhoneFilterBar from '@/components/filters/PhoneFilterBar.vue';
import PreProjectFormDialog from '@/components/page-pre-projects/PreProjectFormDialog.vue';
import PreProjectsDataTable from '@/components/page-pre-projects/PreProjectsDataTable.vue';
import PreProjectsFilters from '@/components/page-pre-projects/PreProjectsFilters.vue';
import PreProjectsSearchBar from '@/components/page-pre-projects/PreProjectsSearchBar.vue';
import PreProjectViews from '@/components/page-pre-projects/PreProjectViews.vue';
import BlurredFeatureOverlay from '@/components/upsell/BlurredFeatureOverlay';
import PreProjectsSamplePreview from '@/components/upsell/PreProjectsSamplePreview';

import { BUILT_IN_VIEW } from '@/store/pre-project-views.module';
import { countSetFilters } from '@/util/filter-count';
import { tenantHasPreProjectsAccess } from '@/util/module-access-util';
import { BI_CALENDAR_URL } from '@/util/tier-mapping';
import { viewport } from '@/util/viewport';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PagePreProjects',
  components: {
    BaseButton,
    BaseDialog,
    BaseIcon,
    BaseInfoBanner,
    BaseViewSwitcher,
    BlurredFeatureOverlay,
    PhoneFilterBar,
    PreProjectFormDialog,
    PreProjectsDataTable,
    PreProjectsFilters,
    PreProjectsSamplePreview,
    PreProjectsSearchBar,
    PreProjectViews,
  },
  data() {
    return {
      deletingPreProject: null,
    };
  },
  computed: {
    ...mapGetters('preProjects', ['preProjects', 'page', 'totalPages', 'pending', 'continuous', 'filters']),
    ...mapGetters('preProjectViews', ['activeViewId', 'activeView']),
    ...mapGetters('tenant', ['trialOn']),
    hasPreProjectsAccess() {
      return tenantHasPreProjectsAccess();
    },
    // Trial tenants with the module get a first-page-only preview (filters,
    // search, views, sorting and pagination inert) — the full dataset is the
    // paid product.
    isTrialRestricted() {
      return this.hasPreProjectsAccess && !!this.trialOn;
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    switchesView() {
      return this.isPhone && !!this.activeView && !this.isTrialRestricted;
    },
    activeViewLabel() {
      const view = this.activeView;
      if (!view) return '';
      return view.nameKey ? this.$t(view.nameKey) : view.name;
    },
    // Budget, years, emitters, locations (one filter for its three levels) and source.
    activeFilterCount() {
      const { minCost, maxCost, years, fromYear, toYear, emitters, provinceIds, regionIds, cityIds, manual } =
        this.filters;
      return countSetFilters([
        minCost != null ? minCost : maxCost,
        (years || []).length ? years : fromYear != null ? fromYear : toYear,
        emitters,
        [...(provinceIds || []), ...(regionIds || []), ...(cityIds || [])],
        manual,
      ]);
    },
    preProjectsBenefits() {
      return [
        this.$t('upsell.pre-projects.benefits.1'),
        this.$t('upsell.pre-projects.benefits.2'),
        this.$t('upsell.pre-projects.benefits.3'),
      ];
    },
  },
  methods: {
    ...mapActions('preProjects', [
      'clearFilters',
      'getPreProjects',
      'deletePreProject',
      'loadMorePreProjects',
      'reloadListedPreProjects',
    ]),
    ...mapActions('preProjectViews', ['loadViews', 'selectView']),
    // ---- custom pre-projects (create / edit / delete) ----
    onAddPreProject() {
      if (this.isTrialRestricted) return;
      this.$refs.preProjectFormDialog.open();
    },
    onEditPreProject(preProject) {
      this.$refs.preProjectFormDialog.open(preProject);
    },
    onPreProjectSaved({ isEdit }) {
      this.$toast.success(this.$t(`page.pre-projects.form.${isEdit ? 'updated-toast' : 'created-toast'}`));
      if (isEdit) this.onRefresh();
      else this.fetchPreProjects(1);
    },
    onConfirmDeletePreProject(preProject) {
      this.deletingPreProject = preProject;
      this.$refs.deletePreProjectDialog.openModal();
    },
    closeDeletePreProject() {
      if (this.$refs.deletePreProjectDialog) this.$refs.deletePreProjectDialog.isModalOpen = false;
    },
    onDeletePreProject() {
      const preProject = this.deletingPreProject;
      this.closeDeletePreProject();
      if (!preProject) return;
      this.deletePreProject(preProject.id)
        .then(() => {
          this.$toast.success(this.$t('page.pre-projects.form.deleted-toast'));
          this.onRefresh();
        })
        .catch((error) => this.$toast.error(error));
      this.deletingPreProject = null;
    },
    fetchPreProjects(page = 1) {
      this.getPreProjects({ page }).catch((error) => this.$toast.error(error));
    },
    // Trial banner CTA — same calendar link as the nav-bar trial banner.
    onBookCall() {
      if (BI_CALENDAR_URL && BI_CALENDAR_URL !== '#') {
        window.open(BI_CALENDAR_URL, '_blank', 'noopener,noreferrer');
      }
    },
    // Refresh re-fetches the page the user is on; on a phone, every page listed, so the
    // reader keeps their place.
    onRefresh() {
      if (this.continuous && this.page > 1) {
        this.reloadListedPreProjects().catch((error) => this.$toast.error(error));
        return;
      }
      this.fetchPreProjects(this.page);
    },
    loadMore() {
      // Asking for a page now would race the search that's running.
      if (this.pending || this.page >= this.totalPages) return;
      this.loadMorePreProjects(this.page + 1);
    },
    // Any filter change restarts from page 1 (the result set changed).
    // Trial preview: refinements are inert (CSS disables the panel; this guard
    // covers keyboard input that still reaches the handlers).
    onFiltersChange() {
      if (this.isTrialRestricted) return;
      this.fetchPreProjects(1);
    },
    // As the filters row's own "Tout effacer".
    onClearAllFilters() {
      this.clearFilters();
      this.onFiltersChange();
    },
    // A view tab was clicked: apply its saved filters, then search from page 1.
    onSelectView(view) {
      if (this.isTrialRestricted) return;
      this.selectView(view.id).then(() => this.fetchPreProjects(1));
    },
    // Load the saved views, start on the built-in "All" tab, then run the
    // search — mirrors the tenders initViewsFlow.
    initViewsFlow() {
      this.loadViews()
        .catch(() => {})
        .then(() => this.selectView(BUILT_IN_VIEW.ALL))
        .then(() => this.fetchPreProjects());
    },
  },
  created() {
    if (this.hasPreProjectsAccess) {
      this.initViewsFlow();
    }
  },
  mounted() {
    if (!this.hasPreProjectsAccess && this.$track && this.$track.preProjectsUpsellView) {
      this.$track.preProjectsUpsellView();
    }
  },
};
</script>

<style lang="scss">
.pre-projects {
  &__header {
    align-items: stretch;
    background: $color-white;
    box-shadow: $shadow-dropdown;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    height: $header-height;
    position: sticky;
    top: $menu-top-bar-height;
    z-index: $header-index;

    @include phone-page-header;
  }

  &__header-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    padding: 16px 32px;

    @include screen-below('tablet') {
      padding: 0 $phone-page-gutter;
    }
  }

  // Matches the standard page-header title (see TendersHeader).
  &__header-title {
    color: $color-normal;
    display: flex;
    font-family: $font-family-raleway;
    font-size: $font-size-page-title;
    font-weight: normal;
    margin: 0;
    overflow: hidden;
    position: relative;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;

    @include phone-page-title;
  }

  // Phones only: its "+" (phone-main-action), off the header's edge.
  &__new {
    align-self: center;
    margin-right: $phone-page-gutter;

    @include phone-main-action;
  }

  &__new-label {
    @include visually-hidden;
  }

  &__main {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 24px 32px 32px 32px;

    @include screen-below('tablet') {
      padding-right: $phone-page-gutter;
      padding-left: $phone-page-gutter;
    }
  }

  &__blur-content {
    pointer-events: none;
    user-select: none;
  }

  // Trial preview notice, between the filters panel and the table.
  &__trial-banner {
    margin-bottom: 16px;
  }
}

// Centered delete confirmation (BaseDialog defaults to a right-side drawer).
.base-dialog__pre-project-delete-confirm {
  position: relative;
  top: 0;
  height: auto;
  width: 460px;
  max-width: 90vw;

  .base-dialog__header {
    height: auto;
    border-bottom: 1px solid $color-border;

    .header__title {
      font-size: 18px;
      margin: 18px 22px;
    }
  }

  .base-dialog__header + .base-dialog__content {
    padding: 20px 22px;
    background: $color-white;
  }

  .base-dialog__actions {
    padding: 16px 22px;
    border-top: 1px solid $color-border;
  }
}

.pre-projects {
  &__delete-text {
    font-size: 14px;
    color: $color-text-body;
    margin: 0;
  }

  &__delete-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  // White surface behind the filters (full-bleed like the tenders views panel).
  &__filters-panel {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    margin: -24px -32px 16px;
    padding: 10px 32px 4px;
    position: relative;
    z-index: 2;

    @include phone-filter-panel;

    // Trial preview: the whole panel (views, filters, search, add) is inert so
    // the visible sample can't be refined into a scrapeable dataset.
    &--disabled {
      opacity: 0.55;
      pointer-events: none;
      user-select: none;
    }
  }
}
</style>
